import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CloseCircle } from 'iconsax-react';

import Chip from '../../../components/atoms/Chip';
import TitleText from '../../../components/atoms/TitleText';
import InfoText from '../../../components/atoms/InfoText';
import { SupportTicket } from '../../../types/support';
import { getStatusType } from './utils';
import { TicketUserCards } from './TicketUserCards';

interface TicketInfoProps {
  ticket: SupportTicket;
  onClose?: () => void;
}

export const TicketInfo: React.FC<TicketInfoProps> = ({ ticket, onClose }) => {
  const { t } = useTranslation();

  const createdDateText = ticket?.createdDate
    ? dayjs(ticket.createdDate).isValid()
      ? dayjs(ticket.createdDate).format('DD MMM YYYY')
      : ticket.createdDate
    : '---';

  const ticketSerial = ticket?.serial?.replace(/^#/, '') || ticket?.id || '---';

  return (
    <div className="w-full min-w-0" data-testid="ticket-info-wrapper">
      {/* Top Header: Reason title and Status badge */}
      <div
        className="flex justify-between items-start gap-4 flex-wrap mb-1"
        data-testid="ticket-info-header"
      >
        <TitleText
          text={ticket?.reason?.name || '---'}
          size="2xl"
          className="!font-bold !text-light-text-primary dark:!text-dark-text-primary break-words"
          data-testid="ticket-info-title"
        />

        <div
          className="flex items-center gap-3 shrink-0"
          data-testid="ticket-info-status-container"
        >
          <Chip
            type={getStatusType(ticket?.status?.code)}
            className="!rounded-full px-3.5 py-1 text-xs font-medium"
            data-testid="ticket-info-status-chip"
          >
            {ticket?.status?.code
              ? t(`support.${ticket.status.code}`, ticket.status?.name || '---')
              : ticket?.status?.name || '---'}
          </Chip>

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center justify-center cursor-pointer"
              aria-label={t('support.cancel')}
              data-testid="ticket-info-close-btn"
            >
              <CloseCircle
                size={24}
                variant="Bold"
                className="text-light-text-primary dark:text-dark-text-primary hover:opacity-80 transition-opacity"
              />
            </button>
          )}
        </div>
      </div>

      {/* Ticket Meta Subtitle */}
      <InfoText
        text={t('support.ticket_meta_info', {
          serial: ticketSerial,
          date: createdDateText,
        })}
        size="sm"
        className="!text-light-text-secondary dark:!text-dark-text-secondary break-words mb-3"
        maxLength="FULL"
        data-testid="ticket-info-meta"
      />

      {/* Description */}
      {ticket?.description && (
        <InfoText
          text={ticket.description}
          size="base"
          className="!text-light-text-primary dark:!text-dark-text-primary leading-relaxed whitespace-pre-line break-words"
          maxLength="FULL"
          data-testid="ticket-info-description"
        />
      )}

      {/* User Information Cards */}
      <TicketUserCards ticket={ticket} className="mt-6" />
    </div>
  );
};

export default TicketInfo;
